import { useState, useRef } from 'react'
import { Upload, Loader2, AlertCircle } from 'lucide-react'
import { useBookmarkStore } from '../store/bookmarkStore'
import { Button } from './Button'
import { parseBookmarkHTML, convertToBookmarks } from '../utils/bookmarkParser'

interface BrowserBookmarkImportProps {
  onClose: () => void
}

interface ImportStats {
  total: number
  imported: number
  skipped: number
  categories: number
}

export function BrowserBookmarkImport({ onClose }: BrowserBookmarkImportProps) {
  const { addBookmark, addCategory, bookmarks, categories } = useBookmarkStore()
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<ImportStats | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setImporting(true)
    setError(null)
    setStats(null)

    try {
      const text = await file.text()
      const parsedBookmarks = await parseBookmarkHTML(text)
      
      // 收集所有唯一的分类
      const uniqueCategories = new Set(parsedBookmarks.map(b => b.category).filter(Boolean))
      
      // 创建新分类并获取分类ID映射
      const categoryMap = new Map<string, string>()
      for (const categoryName of uniqueCategories) {
        if (categoryName) {
          const existingCategory = categories.find(c => c.name === categoryName)
          if (existingCategory) {
            categoryMap.set(categoryName, existingCategory.id)
          } else {
            const categoryId = crypto.randomUUID()
            addCategory({
              name: categoryName,
              description: `从浏览器导入的书签文件夹：${categoryName}`,
            })
            categoryMap.set(categoryName, categoryId)
          }
        }
      }

      // 转换并导入书签
      const bookmarksToImport = convertToBookmarks(parsedBookmarks)
      let imported = 0
      let skipped = 0

      for (let i = 0; i < bookmarksToImport.length; i++) {
        const bookmark = bookmarksToImport[i]
        const parsedBookmark = parsedBookmarks[i]
        
        // 检查是否已存在相同URL的书签
        const existingBookmark = bookmarks.find(b => b.url === bookmark.url)
        if (existingBookmark) {
          skipped++
          continue
        }

        // 添加分类ID
        if (parsedBookmark.category) {
          const categoryId = categoryMap.get(parsedBookmark.category)
          if (categoryId) {
            bookmark.categoryId = categoryId
          }
        }

        addBookmark(bookmark)
        imported++
      }

      setStats({
        total: bookmarksToImport.length,
        imported,
        skipped,
        categories: categoryMap.size,
      })
    } catch (err) {
      console.error('导入错误:', err)
      setError(err instanceof Error ? err.message : '导入书签时发生错误')
    } finally {
      setImporting(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">导入浏览器书签</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p>支持从浏览器导出的书签HTML文件导入。导入过程将：</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>保留原有的文件夹结构（作为分类）</li>
              <li>保持创建时间和修改时间</li>
              <li>导入标签（如果有）</li>
              <li>跳过已存在的相同URL书签</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <input
              type="file"
              accept=".html,.htm"
              onChange={handleFileSelect}
              className="hidden"
              ref={fileInputRef}
              disabled={importing}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center"
            >
              {importing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              选择书签文件
            </Button>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm">{error}</div>
            </div>
          )}

          {stats && (
            <div className="bg-green-50 p-4 rounded">
              <h3 className="font-medium text-green-800 mb-2">导入完成</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>总计处理：{stats.total} 个书签</li>
                <li>成功导入：{stats.imported} 个书签</li>
                <li>已存在跳过：{stats.skipped} 个书签</li>
                <li>相关分类：{stats.categories} 个</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 